import * as sharedServices from "@emanprague/shared-services";
import type { INotificationService } from "@emanprague/shared-services";
import { bound } from "@frui.ts/helpers";
import { ScreenBase, watchBusy } from "@frui.ts/screens";
import { BusyWatcher } from "@frui.ts/screens";
import { attachAutomaticValidator, hasVisibleErrors, validate } from "@frui.ts/validation";
import EtsDeclarationFormData from "entities/etsDeclarationFormData";
import { createSupplyPointsWithIcons } from "helpers/supplyPointHelper";
import { partnerToFullName } from "helpers/utils";
import type { interfaces } from "inversify";
import { action, computed, observable, runInAction } from "mobx";
import EnumsService from "services/enumsService";
import EtsSupplyPointsService from "services/etsSupplyPointsService";
import UserContext from "services/userContext";

export default class EtsDeclarationFormViewModel extends ScreenBase {
  static navigationName = "ets_form";
  navigationName = EtsDeclarationFormViewModel.navigationName;
  busyWatcher = new BusyWatcher();

  @observable data: EtsDeclarationFormData;
  @observable errorMessage?: string;

  constructor(
    public localization: sharedServices.ILocalizationService,
    private userContext: UserContext,
    private enumsService: EnumsService,
    private etsSupplyPointsService: EtsSupplyPointsService,
    private notificationService: INotificationService
  ) {
    super();
    this.data = new EtsDeclarationFormData();

    attachAutomaticValidator(this.data, EtsDeclarationFormData.ValidationRules);
  }

  @bound
  @watchBusy
  protected async onInitialize() {
    await this.etsSupplyPointsService.loadEtsFormData();
  }

  @bound translate(key: string) {
    return this.localization.translateGeneral(`ets_declaration_form.${key}`);
  }

  @bound translateGeneral(key: string) {
    return this.localization.translateGeneral(`general.${key}`);
  }

  @bound @action selectAllSupplyPoints() {
    const year = this.selectedYear;
    if (!year) {
      return [];
    }

    this.data.supplyPointIds = this.etsSupplyPointsService.etsSupplyPoints
      .filter(sp => sp.availableEtsYears.includes(year))
      .map(sp => sp.id);
  }

  @bound @action deselectSupplyPoints() {
    this.data.supplyPointIds = [];
  }

  @bound @action toggleLowConsumption() {
    // We care only for the "more restrictive" direction -> switching to low consumption
    // Disabling low consumption is fine since user then can choose multiple supply points
    if (this.data.lowConsumption) {
      if (!this.availableYears.some(candidate => candidate.id === this.data.year)) {
        this.data.year = undefined;
      }
      this.deselectSupplyPoints();
    }
  }

  get partner() {
    return this.userContext.activePartner;
  }

  @computed
  get availableYears() {
    if (!this.etsSupplyPointsService.availableYears) {
      return [];
    }
    let candidates = this.etsSupplyPointsService.availableYears;
    if (this.data.lowConsumption) {
      candidates = candidates.filter(year => this.etsSupplyPointsService.etsSupplyPoints.some(sp => sp.etsYears[year] === true));
    }
    return candidates.sort().map(year => ({ id: year, label: year }));
  }

  get activePartnerFullName() {
    return partnerToFullName(this.enumsService, this.partner);
  }

  get selectedYear() {
    return this.data.year;
  }

  get supplyPointsWithIcons() {
    const year = this.selectedYear;
    if (!year) {
      return [];
    }

    let filtered = this.etsSupplyPointsService.etsSupplyPoints.filter(sp => sp.availableEtsYears.includes(year));
    if (this.data.lowConsumption) {
      filtered = filtered.filter(sp => sp.etsYears[year] === true);
    }

    return createSupplyPointsWithIcons(this.enumsService, filtered).map(sp => ({
      ...sp,
      title: sp.title ? `${sp.title} (${sp.code})` : sp.code,
    }));
  }

  @bound
  @watchBusy
  async sendRequest() {
    if (!this.partner) {
      return;
    }

    if (this.selectedYear) {
      const supplyPointByIds = new Map(this.etsSupplyPointsService.etsSupplyPoints.map(sp => [sp.id, sp]));
      const sp_data: { [key: string]: boolean } = {};
      (this.data.supplyPointIds ?? []).forEach(id => {
        const sp = supplyPointByIds.get(id);
        if (sp) {
          sp_data[sp.code] = sp.etsYears[this.selectedYear!];
        }
      });
      this.data.supplyPoints = sp_data;
    }

    validate(this.data);
    if (hasVisibleErrors(this.data)) {
      this.notificationService.addNotification(this.translate("invalid"), sharedServices.SeverityLevel.critical);
      setTimeout(() => {
        const firstError = document.querySelector(".invalid-feedback");
        if (firstError) {
          firstError.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 0);
      return;
    }

    const result = await this.etsSupplyPointsService.sendEtsDeclarationForm(this.data);

    if (result) {
      this.busyWatcher.getBusyTicket(); // A trick to keep the busy indicator visible until the page redirects
      window.location.href = result;
    } else {
      runInAction(() => (this.errorMessage = this.translate("error")));
    }
  }

  static Factory({ container }: interfaces.Context) {
    return () => {
      return new EtsDeclarationFormViewModel(
        container.get("ILocalizationService"),
        container.get(UserContext),
        container.get(EnumsService),
        container.get(EtsSupplyPointsService),
        container.get("INotificationService")
      );
    };
  }
}
